import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDemoAuth } from '../../context/DemoAuthContext';
import './AdminDemoPage.css';

const AdminDemoPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useDemoAuth();
  const [activeTab, setActiveTab] = useState('dashboard');

  const handleLogout = () => {
    logout();
    navigate('/demo-login');
  };

  if (!user || !user.isAdmin) {
    return <div>Access denied. Admin privileges required.</div>;
  }

  // Mock data for demo
  const mockUsers = [
    { id: 1, name: 'John Doe', email: 'john@example.com', businessType: 'technology', status: 'active' },
    { id: 2, name: 'Jane Smith', email: 'jane@example.com', businessType: 'healthcare', status: 'active' },
    { id: 3, name: 'Bob Johnson', email: 'bob@example.com', businessType: 'finance', status: 'inactive' },
    { id: 4, name: 'Alice Brown', email: 'alice@example.com', businessType: 'retail', status: 'active' },
  ];

  const mockStats = {
    totalUsers: 1247,
    activeUsers: 1189,
    newUsersThisMonth: 89,
    totalChats: 15678,
    avgChatDuration: '4.2 min'
  };

  const renderDashboard = () => (
    <div className="dashboard-content">
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Users</h3>
          <div className="stat-number">{mockStats.totalUsers}</div>
          <div className="stat-change positive">+12% this month</div>
        </div>
        <div className="stat-card">
          <h3>Active Users</h3>
          <div className="stat-number">{mockStats.activeUsers}</div>
          <div className="stat-change positive">+8% this month</div>
        </div>
        <div className="stat-card">
          <h3>New Users</h3>
          <div className="stat-number">{mockStats.newUsersThisMonth}</div>
          <div className="stat-change positive">+23% this month</div>
        </div>
        <div className="stat-card">
          <h3>Total Chats</h3>
          <div className="stat-number">{mockStats.totalChats}</div>
          <div className="stat-change positive">+15% this month</div>
        </div>
      </div>

      <div className="quick-actions">
        <h3>Quick Actions</h3>
        <div className="action-buttons">
          <button className="action-btn primary">Add New User</button>
          <button className="action-btn secondary">Generate Report</button>
          <button className="action-btn secondary">System Settings</button>
          <button className="action-btn warning">Backup Database</button>
        </div>
      </div>
    </div>
  );

  const renderUsers = () => (
    <div className="users-content">
      <div className="users-header">
        <h3>User Management</h3>
        <button className="add-user-btn">+ Add User</button>
      </div>
      
      <div className="users-table">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Business Type</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {mockUsers.map(user => (
              <tr key={user.id}>
                <td>{user.id}</td>
                <td>{user.name}</td>
                <td>{user.email}</td>
                <td>{user.businessType}</td>
                <td>
                  <span className={`status-badge ${user.status}`}>
                    {user.status}
                  </span>
                </td>
                <td>
                  <div className="action-buttons-small">
                    <button className="action-btn-small edit">Edit</button>
                    <button className="action-btn-small delete">Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderAnalytics = () => (
    <div className="analytics-content">
      <h3>Analytics Overview</h3>
      <div className="analytics-grid">
        <div className="analytics-card">
          <h4>User Growth</h4>
          <div className="chart-placeholder">
            📈 Chart showing user growth over time
          </div>
        </div>
        <div className="analytics-card">
          <h4>Business Type Distribution</h4>
          <div className="chart-placeholder">
            🥧 Pie chart of business types
          </div>
        </div>
        <div className="analytics-card">
          <h4>Chat Activity</h4>
          <div className="chart-placeholder">
            📊 Bar chart of daily chat volume
          </div>
        </div>
        <div className="analytics-card">
          <h4>Geographic Distribution</h4>
          <div className="chart-placeholder">
            🌍 Map showing user locations
          </div>
        </div>
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="settings-content">
      <h3>System Settings</h3>
      <div className="settings-grid">
        <div className="setting-group">
          <h4>General Settings</h4>
          <div className="setting-item">
            <label>Site Name</label>
            <input type="text" defaultValue="Oakland AI" />
          </div>
          <div className="setting-item">
            <label>Maintenance Mode</label>
            <input type="checkbox" />
          </div>
        </div>
        
        <div className="setting-group">
          <h4>Security Settings</h4>
          <div className="setting-item">
            <label>Two-Factor Authentication</label>
            <input type="checkbox" defaultChecked />
          </div>
          <div className="setting-item">
            <label>Session Timeout (minutes)</label>
            <input type="number" defaultValue="30" />
          </div>
        </div>
        
        <div className="setting-group">
          <h4>Email Settings</h4>
          <div className="setting-item">
            <label>SMTP Server</label>
            <input type="text" defaultValue="smtp.gmail.com" />
          </div>
          <div className="setting-item">
            <label>From Email</label>
            <input type="email" defaultValue="noreply@oaklandai.com" />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="admin-demo-container">
      <nav className="admin-demo-nav">
        <div className="nav-brand">
          <h2>Oakland AI - Admin Dashboard</h2>
        </div>
        <div className="nav-user">
          <span>Admin: {user.name}</span>
          <button onClick={handleLogout} className="logout-btn">
            Logout
          </button>
        </div>
      </nav>

      <div className="admin-demo-main">
        <aside className="admin-sidebar">
          <nav className="sidebar-nav">
            <button 
              className={`sidebar-nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
              onClick={() => setActiveTab('dashboard')}
            >
              📊 Dashboard
            </button>
            <button 
              className={`sidebar-nav-item ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => setActiveTab('users')}
            >
              👥 Users
            </button>
            <button 
              className={`sidebar-nav-item ${activeTab === 'analytics' ? 'active' : ''}`}
              onClick={() => setActiveTab('analytics')}
            >
              📈 Analytics
            </button>
            <button 
              className={`sidebar-nav-item ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveTab('settings')}
            >
              ⚙️ Settings
            </button>
          </nav>
        </aside>

        <main className="admin-content">
          {activeTab === 'dashboard' && renderDashboard()}
          {activeTab === 'users' && renderUsers()}
          {activeTab === 'analytics' && renderAnalytics()}
          {activeTab === 'settings' && renderSettings()}
        </main>
      </div>
    </div>
  );
};

export default AdminDemoPage;
